/**
 * Copy to clipboard (§6.10). The published prototype runs in a sandboxed frame
 * where the async Clipboard API can be refused, so this falls back to a hidden
 * textarea and `execCommand('copy')`, and says so when both fail.
 */
import { middleTruncate } from '@/lib/format';
import { toast } from '@/lib/toast';

async function writeWithApi(value: string): Promise<boolean> {
  try {
    if (!navigator.clipboard?.writeText) return false;
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

function writeWithTextarea(value: string): boolean {
  const active =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
  const area = document.createElement('textarea');
  area.value = value;
  area.setAttribute('readonly', '');
  area.setAttribute('aria-hidden', 'true');
  Object.assign(area.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    opacity: '0',
    pointerEvents: 'none',
  });
  document.body.append(area);
  area.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  area.remove();
  active?.focus({ preventScroll: true });
  return ok;
}

/** Copies text without any toast. Resolves to whether it worked. */
async function writeClipboard(value: string): Promise<boolean> {
  if (await writeWithApi(value)) return true;
  return writeWithTextarea(value);
}

/**
 * The label for "Copied …": the given label, or the value middle-truncated
 * ("SAOO11B…60BE4").
 */
export function copyLabel(value: string, label?: string): string {
  return label ?? middleTruncate(value, 7, 5);
}

/**
 * Copies and confirms with the toast "Copied <label>" (2 s). On failure the
 * error toast tells the person to copy by hand.
 */
export async function copyText(
  value: string,
  label?: string,
): Promise<boolean> {
  const ok = await writeClipboard(value);
  if (ok) toast.copy(copyLabel(value, label));
  else
    toast.error('Could not copy.', {
      description:
        'This browser blocked the clipboard. Select the text and copy it.',
    });
  return ok;
}
